import { NavLink, Link, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Briefcase, MessageSquare, Sparkles, LogOut, Loader2, RefreshCw, Receipt, Palette, Package, HelpCircle, User } from "lucide-react";
import { FlowtalisWordmark } from "@/components/layout/Header";
import NotificationBell from "@/components/NotificationBell";
import UniversalSearch from "@/components/UniversalSearch";

const NAV = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/portal/projects", label: "Projects", icon: Briefcase },
  { to: "/portal/artwork", label: "Artwork", icon: Palette },
  { to: "/portal/vault", label: "Brand Vault", icon: Package },
  { to: "/portal/reorder", label: "Orders & Reorders", icon: RefreshCw },
  { to: "/portal/invoices", label: "Invoices", icon: Receipt },
  { to: "/portal/support", label: "Support", icon: HelpCircle },
  { to: "/portal/messages", label: "Messages", icon: MessageSquare },
  { to: "/portal/ai", label: "AI Assistant", icon: Sparkles },
  { to: "/portal/profile", label: "Profile", icon: User },
];

const PortalLayout = ({ children }) => {
  const { user, loading, logout, isAdmin } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  // Admins/founder land in Command Center — not the Client Portal. Prevents the
  // "Welcome, FLOWTALIS" client dashboard from showing up for staff accounts.
  if (isAdmin) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen bg-[var(--ft-soft-gray)] flex">
      <aside className="ft-sidebar w-64 hidden md:flex md:flex-col fixed inset-y-0 left-0 z-30">
        <Link to="/" className="h-16 flex items-center px-6 border-b border-white/8">
          <span
            className="font-bold tracking-[0.22em] text-[1.1rem] leading-none"
            style={{ color: "var(--ft-gold)", fontFamily: "Manrope, sans-serif" }}
          >
            FLOWTALIS<span className="text-[0.55em] align-super ml-0.5 font-medium">™</span>
          </span>
        </Link>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `ft-side-link ${isActive ? "active" : ""}`}
            >
              <item.icon size={18} /> {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/8">
          <div className="text-[0.625rem] text-white/40 mb-2 uppercase tracking-[0.24em] font-semibold">Signed in</div>
          <div className="text-sm font-semibold text-white">{user.full_name}</div>
          <div className="text-xs text-white/50 truncate">{user.email}</div>
          <button onClick={() => { logout(); nav("/"); }} data-testid="portal-logout" className="mt-3 ft-side-link w-full justify-start">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 md:ml-64">
        <div className="md:hidden border-b border-[var(--ft-border)] bg-white">
          <div className="h-16 flex items-center justify-between px-6">
            <FlowtalisWordmark variant="light" />
            <div className="flex items-center gap-2">
              <NotificationBell />
              <button onClick={() => { logout(); nav("/"); }} className="ft-btn-ghost" aria-label="Sign out"><LogOut size={16} /></button>
            </div>
          </div>
          <div className="px-4 pb-3">
            <UniversalSearch />
          </div>
        </div>
        <div className="hidden md:flex items-center justify-between gap-4 px-6 pt-4">
          <UniversalSearch />
          <NotificationBell />
        </div>
        <div className="md:hidden bg-white border-b border-[var(--ft-border)] px-4 py-3 flex gap-2 overflow-x-auto">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `ft-side-link !py-2 !px-3 whitespace-nowrap ${isActive ? "active" : ""}`}
            >
              <item.icon size={16} /> {item.label}
            </NavLink>
          ))}
        </div>
        <main className="p-6 md:p-10">{children}</main>
      </div>
    </div>
  );
};

export default PortalLayout;
